/**
 * The Yalo design system, as data.
 *
 * Transcribed from `docs/design/reference.html` — the design's own `:root`
 * block — rather than reimplemented. Values are copied, not rounded: a scale
 * that drifts from its source stops being a scale.
 *
 * This file is plain data with no CSS and no MUI, for the same reason the rest
 * of `@commerce/tokens` is: a React Native shell has to be able to read the same
 * palette. `toMuiTheme` is where it becomes a web theme.
 */

/** Brand colours. The names are the brand book's, not descriptions of use. */
export const YALO_PALETTE = {
  black: '#0E151C',
  carbon: '#0E151C',
  sand: '#FAF8F4',
  white: '#FFFFFF',
  mist: '#EDF0F2',
  orange: '#EDAE3E',
  green: '#40E4A9',
  blue: '#486CE9',
  cyan: '#00C2EE',
  purple: '#D75DFF',
  peach: '#FFB87B',
  pink: '#F5D3CB',
  neutral100: '#06090B',
  neutral90: '#0E151C',
  neutral30: '#DDE2E5',
  neutral20: '#EEF1F3',
} as const

export type YaloColorName = keyof typeof YALO_PALETTE

/**
 * Semantic foreground and background slots.
 *
 * The design expresses secondary text as an alpha over carbon rather than a
 * flat grey, so it stays correct on any surface tint. Kept as `rgba()` strings
 * for that reason — flattening them to hex would break on the sand canvas.
 */
export const YALO_SEMANTIC = {
  fg1: '#0E151C',
  fg2: 'rgba(14, 21, 28, 0.72)',
  fg3: 'rgba(14, 21, 28, 0.52)',
  fg4: 'rgba(14, 21, 28, 0.32)',
  fgOnDark1: '#FFFFFF',
  fgOnDark2: 'rgba(255, 255, 255, 0.72)',
  fgOnDark3: 'rgba(255, 255, 255, 0.48)',
  bgCanvas: '#FAF8F4',
  bgSurface: '#FFFFFF',
  bgSunken: '#EDF0F2',
  bgInverse: '#0E151C',
  bgTint: 'rgba(14, 21, 28, 0.04)',
  borderSubtle: 'rgba(14, 21, 28, 0.08)',
  borderDefault: '#DDE2E5',
  borderStrong: 'rgba(14, 21, 28, 0.28)',
  borderOnDark: 'rgba(255, 255, 255, 0.14)',
  statusSuccess: '#40E4A9',
  statusWarning: '#EDAE3E',
  statusInfo: '#00C2EE',
  statusDanger: '#E0506E',
} as const

/** The AI signature gradient. Reserved for agent moments, not decoration. */
export const YALO_GRADIENT_AI =
  'linear-gradient(135deg, #00C2EE 0%, #D75DFF 55%, #FFB87B 100%)'

export const YALO_GRADIENT_AI_SOFT =
  'linear-gradient(135deg, #BFE9F8 0%, #E9C6FB 55%, #FFD9B5 100%)'

/** 4px base. Index is the step, so `YALO_SPACE[4]` is 16px. */
export const YALO_SPACE = [0, 4, 8, 12, 16, 20, 24, 32, 40, 48, 64, 80, 96, 128] as const

export const YALO_RADIUS = {
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 24,
  xxl: 32,
  pill: 999,
} as const

export const YALO_SHADOW = {
  xs: '0 1px 2px rgba(14, 21, 28, 0.04)',
  sm: '0 2px 6px rgba(14, 21, 28, 0.05), 0 1px 2px rgba(14, 21, 28, 0.04)',
  md: '0 8px 20px rgba(14, 21, 28, 0.07), 0 2px 4px rgba(14, 21, 28, 0.04)',
  lg: '0 18px 40px rgba(14, 21, 28, 0.10), 0 4px 8px rgba(14, 21, 28, 0.05)',
  xl: '0 28px 64px rgba(14, 21, 28, 0.14)',
  glowAi: '0 0 0 1px rgba(215, 93, 255, 0.35), 0 8px 32px rgba(0, 194, 238, 0.20)',
} as const

/**
 * Tomato Grotesk is the brand face and it is **licensed**. The design bundles
 * the .otf files; they are deliberately not vendored into this repo, because
 * shipping a licensed font in a public bundle is a decision for whoever holds
 * the licence, not for a build script.
 *
 * DM Sans is the brand book's own documented fallback for environments without
 * the licence, which is exactly this one until someone says otherwise.
 */
export const YALO_FONT_STACK =
  '"Tomato Grotesk", "DM Sans", ui-sans-serif, system-ui, -apple-system, "Segoe UI", sans-serif'

export const YALO_FONT_MONO =
  '"SF Mono", ui-monospace, Menlo, Consolas, "Liberation Mono", monospace'

export interface YaloTextStyle {
  fontSize: number
  lineHeight: number
  /** Letter-spacing in px. Negative for the display sizes, as authored. */
  letterSpacing: number
  fontWeight?: number
}

/**
 * The paragraph scale. The design also carries a marketing heading scale
 * (74px h1) and a presentation scale; neither belongs in a storefront running
 * in a phone-sized webview, so only the paragraph scale and the pptx headings
 * — which are the ones sized for a screen — come across.
 */
export const YALO_TEXT = {
  h1: { fontSize: 42, lineHeight: 50, letterSpacing: -1, fontWeight: 600 },
  h2: { fontSize: 28, lineHeight: 34, letterSpacing: -0.5, fontWeight: 600 },
  h3: { fontSize: 20, lineHeight: 28, letterSpacing: -0.3, fontWeight: 600 },
  p2xl: { fontSize: 24, lineHeight: 32, letterSpacing: -0.44 },
  pXl: { fontSize: 20, lineHeight: 32, letterSpacing: -0.32 },
  pLg: { fontSize: 18, lineHeight: 24, letterSpacing: -0.26 },
  pMd: { fontSize: 16, lineHeight: 24, letterSpacing: -0.18 },
  pSm: { fontSize: 14, lineHeight: 20, letterSpacing: -0.08 },
  pXs: { fontSize: 12, lineHeight: 16, letterSpacing: 0 },
  eyebrow: { fontSize: 12, lineHeight: 16, letterSpacing: 1.44, fontWeight: 600 },
} as const satisfies Record<string, YaloTextStyle>

export type YaloTextRole = keyof typeof YALO_TEXT

/**
 * The accent is the one colour a storefront chooses. The design offers carbon,
 * royal blue and electric violet, and derives two shades from whichever is
 * picked — a darker one for text on light, a soft one for tinted surfaces.
 *
 * The design does this with `color-mix()`, which cannot be read back in JS.
 * These are the same two derivations computed here so a React Native shell,
 * which has no `color-mix`, gets identical values.
 */
export const YALO_ACCENTS = [YALO_PALETTE.carbon, YALO_PALETTE.blue, YALO_PALETTE.purple] as const

const clamp = (n: number): number => Math.max(0, Math.min(255, Math.round(n)))

const parseHex = (hex: string): [number, number, number] => {
  const value = hex.replace('#', '')
  const full =
    value.length === 3
      ? value
          .split('')
          .map((c) => c + c)
          .join('')
      : value
  return [
    parseInt(full.slice(0, 2), 16),
    parseInt(full.slice(2, 4), 16),
    parseInt(full.slice(4, 6), 16),
  ]
}

const toHex = (rgb: [number, number, number]): string =>
  '#' + rgb.map((c) => clamp(c).toString(16).padStart(2, '0')).join('').toUpperCase()

/** Mix two hex colours in sRGB. `amount` is how much of `a` survives. */
export const mixHex = (a: string, b: string, amount: number): string => {
  const [ar, ag, ab] = parseHex(a)
  const [br, bg, bb] = parseHex(b)
  return toHex([
    ar * amount + br * (1 - amount),
    ag * amount + bg * (1 - amount),
    ab * amount + bb * (1 - amount),
  ])
}

export interface YaloAccent {
  /** The chosen accent, as authored. */
  main: string
  /** Darkened toward carbon — legible as text on a light surface. */
  dark: string
  /** 12% over white — the tint behind selected chips and highlight cards. */
  soft: string
  /** Text that sits on top of `main`. */
  contrast: string
}

/**
 * Carbon is its own dark shade: mixing it toward carbon would leave it
 * unchanged and the design special-cases it, so this does too.
 */
export const yaloAccent = (main: string = YALO_PALETTE.purple): YaloAccent => {
  const isCarbon = main.toUpperCase() === YALO_PALETTE.carbon
  return {
    main,
    dark: isCarbon ? YALO_SEMANTIC.fg1 : mixHex(main, YALO_PALETTE.carbon, 0.62),
    soft: mixHex(main, YALO_PALETTE.white, 0.12),
    contrast: YALO_PALETTE.white,
  }
}
